import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

interface DrillDownModalProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  metric?: { value: string | number; label: string; trend?: number }
  chart?: React.ReactNode
  table?: React.ReactNode
  actions?: Array<{ label: string; onClick: () => void; variant?: 'primary' | 'secondary' | 'ghost' }>
  filters?: React.ReactNode
}

const btnVariants = {
  primary: 'bg-brick hover:bg-brick-hover text-white',
  secondary: 'border border-brick text-brick hover:bg-gold-light',
  ghost: 'text-ink hover:bg-paper-warm',
}

export function DrillDownModal({ open, onClose, title, subtitle, metric, chart, table, actions, filters }: DrillDownModalProps) {
  const ref = useRef<HTMLDivElement>(null)

  // Focus trap + Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    ref.current?.focus()
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" role="dialog" aria-label={title}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div
        ref={ref}
        tabIndex={-1}
        className={cn(
          'relative z-10 bg-white w-full sm:w-[640px] sm:max-h-[80vh] max-h-[90vh]',
          'rounded-t-2xl sm:rounded-2xl border border-ink-border shadow-elevated',
          'overflow-y-auto animate-slide-up',
        )}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-ink-border px-6 py-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-ink">{title}</h2>
            {subtitle && <p className="text-sm text-ink-muted mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-paper-warm -mr-1.5 -mt-0.5">
            <X size={18} className="text-ink-faint" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Metric highlight */}
          {metric && (
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold text-ink">{metric.value}</span>
              <span className="text-sm text-ink-muted">{metric.label}</span>
              {metric.trend !== undefined && (
                <span className={cn(
                  'text-xs font-semibold px-1.5 py-0.5 rounded',
                  metric.trend >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50',
                )}>
                  {metric.trend >= 0 ? '▲' : '▼'} {Math.abs(metric.trend).toFixed(1)}%
                </span>
              )}
            </div>
          )}

          {/* Filters */}
          {filters && <div>{filters}</div>}

          {/* Chart */}
          {chart && <div className="bg-paper rounded-card p-4">{chart}</div>}

          {/* Table */}
          {table && <div>{table}</div>}

          {/* Actions */}
          {actions && actions.length > 0 && (
            <div className="flex items-center gap-2 pt-2 border-t border-ink-border">
              {actions.map((action, i) => (
                <button
                  key={i}
                  onClick={action.onClick}
                  className={cn(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                    btnVariants[action.variant ?? 'secondary'],
                  )}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
